import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../api";
import { formatCards, formatDateRange } from "../utils/format";
import { Skeleton } from "./Skeleton";

interface AirdropStatsPanelProps {
  airdropId: string;
}

// Single-series chart: the brand gold
const CHART_COLOR = "#f0b84a";

const formatCompact = (value: number) =>
  value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000
    ? `${(value / 1_000).toFixed(0)}k`
    : `${value}`;

// Eden treaty revives ISO-looking strings into Date objects client-side, so a
// timeline "YYYY-MM-DD" may arrive as either a string or a Date.
const toUtcDate = (value: unknown): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? new Date(`${text}T00:00:00Z`)
    : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDay = (value: unknown) => {
  const date = toUtcDate(value);
  return date
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : String(value);
};

function StatTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: "yellow" | "teal";
}) {
  const color = accent === "yellow" ? "text-yellow-400" : "text-gray-50";
  return (
    <div className="card-inset p-3">
      <div className="text-gray-400 text-xs mb-1">{label}</div>
      <div className={`text-xl font-semibold num ${color}`}>{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

export function AirdropStatsPanel({ airdropId }: AirdropStatsPanelProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["airdrop-stats", airdropId],
    queryFn: async () => {
      const { data, error } = await api.airdrops({ airdropId }).stats.get();
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return <Skeleton kind="chart" />;
  }

  if (error) {
    return (
      <p className="text-red-400">Could not load airdrop stats: {String(error)}</p>
    );
  }

  if (!data) return null;

  const { airdrop, claims } = data;
  const notIndexed = airdrop.recipientCount === 0;
  const maxBucketWallets = Math.max(1, ...data.buckets.map((b) => b.wallets));

  return (
    <div className="space-y-6">
      <div className="text-xs text-gray-500">
        Points period {formatDateRange(airdrop.periodFrom, airdrop.periodTo)}
        {airdrop.lastSyncedAt && (
          <>, last indexed {new Date(airdrop.lastSyncedAt).toLocaleString()}</>
        )}
      </div>

      {notIndexed ? (
        <p className="text-gray-500">
          No claims indexed for this airdrop yet.
        </p>
      ) : (
        <>
          {/* Headline stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="$CARDS distributed"
              value={formatCards(airdrop.totalDistributed)}
              accent="yellow"
            />
            <StatTile
              label="Recipients"
              value={airdrop.recipientCount.toLocaleString()}
              hint={`${claims.claimTxs.toLocaleString()} claim transactions`}
            />
            <StatTile
              label="Platform points gained"
              value={data.totalPlatformGain.toLocaleString()}
              hint={`${data.rankedWallets.toLocaleString()} wallets ranked`}
            />
            <StatTile
              label="Avg $CARDS per 1k points"
              value={
                data.cardsPerThousandPoints !== null
                  ? formatCards(data.cardsPerThousandPoints)
                  : "—"
              }
            />
            <StatTile label="Median claim" value={formatCards(claims.median)} />
            <StatTile label="Average claim" value={formatCards(claims.avg)} />
            <StatTile
              label="Top 10% claimed at least"
              value={formatCards(claims.p90)}
              hint={`largest ${formatCards(claims.max)}`}
            />
            <StatTile
              label="Claim window"
              value={
                claims.firstClaimAt
                  ? new Date(claims.firstClaimAt).toLocaleDateString()
                  : "—"
              }
              hint={
                claims.lastClaimAt
                  ? `to ${new Date(claims.lastClaimAt).toLocaleDateString()}`
                  : undefined
              }
            />
          </div>

          {/* Timeline + distribution */}
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              <h3 className="text-sm font-medium text-gray-300 mb-2">
                Cumulative $CARDS claimed
              </h3>
              {data.timeline.length < 2 ? (
                <div className="h-56 flex items-center justify-center text-gray-500 text-sm">
                  Not enough data for a timeline yet
                </div>
              ) : (
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={data.timeline}
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id={`airdrop-fill-${airdropId}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor={CHART_COLOR} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={CHART_COLOR} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2739" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#5b6982"
                        tick={{ fill: "#8b98b0" }}
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={formatDay}
                        minTickGap={32}
                      />
                      <YAxis
                        stroke="#5b6982"
                        tick={{ fill: "#8b98b0" }}
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={formatCompact}
                        width={48}
                      />
                      <Tooltip
                        cursor={{ stroke: "#5b6982", strokeDasharray: "3 3" }}
                        contentStyle={{
                          backgroundColor: "#131826",
                          border: "1px solid #2c374d",
                          borderRadius: 10,
                        }}
                        labelStyle={{ color: "#8b98b0" }}
                        itemStyle={{ color: "#eaeef6" }}
                        labelFormatter={(label) => formatDay(String(label))}
                        formatter={(value, _name, item) => {
                          const point = item.payload as (typeof data.timeline)[number];
                          return [
                            `${formatCards(Number(value))} total, +${formatCards(point.cards)} from ${point.claims.toLocaleString()} claim${point.claims === 1 ? "" : "s"} that day`,
                            "$CARDS claimed",
                          ];
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="cumulativeCards"
                        stroke={CHART_COLOR}
                        strokeWidth={2}
                        fill={`url(#airdrop-fill-${airdropId})`}
                        dot={false}
                        activeDot={{ r: 4, stroke: "#131826", strokeWidth: 2 }}
                        name="$CARDS claimed"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <h3 className="text-sm font-medium text-gray-300 mb-2">
                Recipients by claim size
              </h3>
              <div className="space-y-2">
                {data.buckets.map((bucket) => {
                  const pct =
                    airdrop.recipientCount > 0
                      ? (bucket.wallets / airdrop.recipientCount) * 100
                      : 0;
                  return (
                    <div key={bucket.label} className="text-sm">
                      <div className="flex justify-between text-gray-400 mb-1">
                        <span>{bucket.label} $CARDS</span>
                        <span className="num">
                          <span className="text-gray-100 font-medium">
                            {bucket.wallets.toLocaleString()}
                          </span>{" "}
                          wallets, {pct.toFixed(1)}%,{" "}
                          <span className="text-gray-300">
                            {formatCards(bucket.cards)}
                          </span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${(bucket.wallets / maxBucketWallets) * 100}%`,
                            backgroundColor: CHART_COLOR,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
